import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Modal, Button, Form } from 'react-bootstrap';
import { FaCheck, FaHeart, FaCrown, FaStar, FaRocket } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const Pricing = () => {
    const navigate = useNavigate();
    const [showDonationModal, setShowDonationModal] = useState(false);
    const [donationAmount, setDonationAmount] = useState('5');
    const [donationEmail, setDonationEmail] = useState('');
    const [isProcessingDonation, setIsProcessingDonation] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        setIsAuthenticated(!!token);
    }, []);

    const handleSubscribe = async (planId) => {
        if (!isAuthenticated) {
            navigate('/signup');
            return;
        }

        setIsProcessingPayment(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/payment/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ planId }),
            });

            const data = await response.json();

            if (response.ok) {
                window.location.href = data.url;
            } else {
                alert('Failed to create checkout session: ' + data.error);
            }
        } catch (error) {
            // console.error('Payment error:', error);
            alert('Failed to process payment. Please try again.');
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const handleDonation = () => {
        setShowDonationModal(true);
    };

    const handleDonationSubmit = async (e) => {
        e.preventDefault();
        setIsProcessingDonation(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/payment/create-donation-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    amount: parseFloat(donationAmount),
                    email: donationEmail
                }),
            });

            const data = await response.json();

            if (response.ok) {
                window.location.href = data.url;
            } else {
                alert('Failed to create donation session: ' + data.error);
            }
        } catch (error) {
            // console.error('Donation error:', error);
            alert('Failed to process donation. Please try again.');
        } finally {
            setIsProcessingDonation(false);
        }
    };
    return (
        <>
        <div className="pricing-hero">
            <Container>
                <Row className="py-5">
                    <Col lg={8} className="mx-auto text-center">
                        <h1 className="display-3 fw-bold mb-4" style={{background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>Unlock Your AI Journey</h1>
                        <p className="lead mb-5" style={{fontSize: '1.25rem', color: '#6c757d'}}>
                            Discover cutting-edge AI tools that transform your workflow. Start exploring today.
                        </p>
                    </Col>
                </Row>
            </Container>
        </div>
        
        <Container className="py-5">

            {/* Pricing Cards */}
            <Row className="mb-5 justify-content-center">
                {/* Free Trial */}
                <Col lg={3} md={6} className="mb-4">
                    <Card className="pricing-card h-100 border-0 shadow-sm">
                        <Card.Body className="p-3">
                            <div className="text-center mb-3">
                                <FaRocket style={{fontSize: '2.5rem', color: '#4ECDC4'}} />
                                <h3 className="mt-3 mb-2">Starter</h3>
                                <div className="pricing-price">
                                    <span className="h2 fw-bold">$0</span>
                                    <span className="text-muted">/7 days</span>
                                </div>
                            </div>
                            <ul className="list-unstyled mb-3">
                                <li className="mb-2"><FaCheck className="text-success me-2" />Explore AI tools freely</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Daily discoveries</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Email newsletter</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Community access</li>
                            </ul>
                            <Button 
                                variant="outline-primary" 
                                size="md" 
                                className="w-100"
                                onClick={() => navigate('/signup')}
                            >
                                Get Started
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Basic Plan */}
                <Col lg={3} md={6} className="mb-4">
                    <Card className="pricing-card pricing-card-popular h-100 border-0 shadow">
                        <div className="pricing-badge">MOST POPULAR</div>
                        <Card.Body className="p-3">
                            <div className="text-center mb-3">
                                <FaStar style={{fontSize: '2.5rem', color: '#FF6B6B'}} />
                                <h3 className="mt-3 mb-2">Pro</h3>
                                <div className="pricing-price">
                                    <span className="h2 fw-bold">$3.99</span>
                                    <span className="text-muted">/month</span>
                                </div>
                            </div>
                            <ul className="list-unstyled mb-3">
                                <li className="mb-2"><FaCheck className="text-success me-2" />Everything in Starter</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Unlimited discoveries</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Priority updates</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Ad-free experience</li>
                            </ul>
                            <Button 
                                variant="primary" 
                                size="md" 
                                className="w-100"
                                onClick={() => handleSubscribe('basic')}
                                disabled={isProcessingPayment}
                            >
                                {isProcessingPayment ? 'Processing...' : 'Unlock Pro'}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Advanced Plan */}
                <Col lg={3} md={6} className="mb-4">
                    <Card className="pricing-card h-100 border-0 shadow-sm">
                        <Card.Body className="p-3">
                            <div className="text-center mb-3">
                                <FaCrown style={{fontSize: '2.5rem', color: '#ffd700'}} />
                                <h3 className="mt-3 mb-2">Enterprise</h3>
                                <div className="pricing-price">
                                    <span className="h2 fw-bold">$9.99</span>
                                    <span className="text-muted">/month</span>
                                </div>
                            </div>
                            <ul className="list-unstyled mb-3">
                                <li className="mb-2"><FaCheck className="text-success me-2" />Everything in Pro</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Early feature access</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Custom AI insights</li>
                                <li className="mb-2"><FaCheck className="text-success me-2" />Dedicated support</li>
                            </ul>
                            <Button 
                                variant="warning" 
                                size="md" 
                                className="w-100"
                                onClick={() => handleSubscribe('advanced')}
                                disabled={isProcessingPayment}
                            >
                                {isProcessingPayment ? 'Processing...' : 'Go Enterprise'}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Support Section */}
            <Row className="mb-5">
                <Col lg={8} className="mx-auto text-center">
                    <div className="support-section p-4 rounded" style={{background: 'linear-gradient(135deg, #FF6B6B15 0%, #4ECDC415 100%)'}}>
                        <FaHeart style={{fontSize: '3rem', color: '#e74c3c'}} className="mb-3" />
                        <h3 className="mb-3">Enjoying Agenti?</h3>
                        <p className="lead mb-4">Help us keep discovering and sharing the best AI tools with the community.</p>
                        <Button 
                            variant="outline-danger" 
                            size="md"
                            onClick={() => handleDonation()}
                        >
                            � Support Agenti
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* FAQ Section */}
            <Row className="mb-5">
                <Col lg={10} className="mx-auto">
                    <h3 className="text-center mb-5">Frequently Asked Questions</h3>
                    <Row>
                        <Col md={6} className="mb-4">
                            <h5>Can I cancel anytime?</h5>
                            <p className="text-muted">Yes! Cancel your subscription anytime with no questions asked. You'll continue to have access until the end of your billing period.</p>
                        </Col>
                        <Col md={6} className="mb-4">
                            <h5>What payment methods do you accept?</h5>
                            <p className="text-muted">We accept all major credit cards through Stripe, our secure payment processor.</p>
                        </Col>
                        <Col md={6} className="mb-4">
                            <h5>Is the free trial really free?</h5>
                            <p className="text-muted">Absolutely! No credit card required for the 7-day trial. Upgrade anytime to continue enjoying our service.</p>
                        </Col>
                        <Col md={6} className="mb-4">
                            <h5>Can I upgrade or downgrade my plan?</h5>
                            <p className="text-muted">Yes! Switch between plans anytime. Changes take effect at the start of your next billing cycle.</p>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* Donation Modal */}
            <Modal show={showDonationModal} onHide={() => setShowDonationModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>� Support Agenti</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-4">
                    Your support helps us continue discovering amazing AI tools and keeping Agenti free for everyone.
                </p>
                
                <Form onSubmit={handleDonationSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Donation Amount (USD)</Form.Label>
                        <Form.Select 
                            value={donationAmount} 
                            onChange={(e) => setDonationAmount(e.target.value)}
                        >
                            <option value="5">$5 - Coffee break ☕</option>
                            <option value="25">$25 - Fuel our discoveries 🚀</option>
                            <option value="100">$100 - Champion of AI 🏆</option>
                        </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control 
                            type="email" 
                            placeholder="your@email.com"
                            value={donationEmail}
                            onChange={(e) => setDonationEmail(e.target.value)}
                            required
                        />
                        <Form.Text className="text-muted">
                            We'll send you a thank you email and receipt for your donation.
                        </Form.Text>
                    </Form.Group>
                    
                    <div className="d-grid gap-2">
                        <Button 
                            variant="warning" 
                            type="submit" 
                            disabled={isProcessingDonation}
                        >
                            {isProcessingDonation ? 'Processing...' : `Donate $${donationAmount}`}
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowDonationModal(false)}
                        >
                            Maybe Later
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    </Container>
    </>
    );
};

export default Pricing; 